import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  MoreVert,
  LocationOn,
  Payments,
  CalendarToday,
  VerifiedUser,
  Delete,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import EventFormDialog from '../components/EventFormDialog';

interface Event {
  _id: string;
  eventName: string;
  date: string;
  price: number;
  slots: number;
  availableSlots: number;
  images: string[];
  description: string;
  skillLevel: string;
  classType?: {
    title: string;
  };
  instructor?: Array<{
    name: string;
    image: string;
  }>;
  thingsToBring: string[];
  cancellationPolicy: string;
  payAtEvent: boolean;
  address: string;
  city: string;
  state: string;
  pincode: string;
  eventStatus: boolean;
  interestedUsers?: any[];
  studio?: {
    _id?: string;
    studioName: string;
    images: string[];
  };
}

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEventDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getEventById(eventId!);
      setEvent(response.event);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch event details');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, fetchEventDetails]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      // Pinned to the event's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteEvent(eventId!, studioIdOf(event?.studio));
      navigate(-1);
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Event not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            {event.images?.[0] && (
              <Avatar
                src={event.images[0]}
                variant="rounded"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                }}
              />
            )}
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h4" fontWeight={700}>
                  {event.eventName}
                </Typography>
                {event.skillLevel && (
                  <Chip 
                    label={event.skillLevel} 
                    size="small" 
                    sx={{ backgroundColor: colors.backgroundTertiary, border: `1px solid ${colors.divider}` }} 
                  />
                )}
                <Chip
                  label={event.eventStatus ? 'Active' : 'Cancelled'}
                  size="small"
                  color={event.eventStatus ? 'success' : 'error'}
                />
              </Box>
              {event.studio && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {event.studio.studioName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="outlined" size="small" startIcon={<Edit />} onClick={handleEditClick}>
              Edit
            </Button>
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditClick}>
                <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
              </MenuItem>
              <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column - Details */}
        <Box>
          {/* Images - Compact */}
          {event.images && event.images.length > 0 && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 0,
                  }}
                >
                  {event.images.map((img, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={img}
                      alt={`${event.eventName} ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: { xs: 120, sm: 150 },
                        objectFit: 'cover',
                        border: `1px solid ${colors.divider}`,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Description - Compact */}
          {event.description && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  About This Event
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                  {event.description}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Things to Bring & Cancellation - Side by Side */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {event.thingsToBring && event.thingsToBring.length > 0 && (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Things to Bring
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {event.thingsToBring.map((item, index) => (
                      <Typography key={index} component="li" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {event.cancellationPolicy && (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Cancellation Policy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {event.cancellationPolicy}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        {/* Right Column - Info */}
        <Box>
          {/* Event Info Card */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Event Details
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={1.5}>
                {/* Date & Time */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <CalendarToday sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date & Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(event.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC'
                      })}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(event.date).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC'
                      })}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Location */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <LocationOn sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Location
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {event.address}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.city}{event.state ? `, ${event.state}` : ''} - {event.pincode}
                    </Typography>
                  </Box>
                </Box>

                {event.instructor && event.instructor.length > 0 && (
                  <>
                    <Divider />
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <VerifiedUser sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Instructor{event.instructor.length > 1 ? 's' : ''}
                        </Typography>
                        {event.instructor.map((inst, index) => (
                          <Typography key={index} variant="body2" fontWeight={600}>
                            {inst.name}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                <Divider />

                {/* Payment */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <Payments sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Payment
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ₹{event.price?.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.payAtEvent ? 'Pay at venue' : 'Pay online'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Availability Card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Availability
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Slots
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={colors.textPrimary}>
                    {event.slots}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary">
                    Available
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={colors.success}>
                    {event.availableSlots !== undefined ? event.availableSlots : event.slots}
                  </Typography>
                </Box>
              </Box>

              {event.interestedUsers && event.interestedUsers.length > 0 && (
                <Box mt={1.5} pt={1.5} borderTop={`1px solid ${colors.divider}`}>
                  <Typography variant="caption" color="text.secondary">
                    Interested Users
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {event.interestedUsers.length}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <EventFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={fetchEventDetails}
        event={event}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this event? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventDetailPage;

